import { yupResolver } from "@hookform/resolvers/yup";
import { designTokens, RiDeleteBinLine } from "@itandi/itandi-bb-ui";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { Flex } from "@itandi/itandi-bb-ui/Flex";
import { FormControl } from "@itandi/itandi-bb-ui/FormControl";
import { FormControlText } from "@itandi/itandi-bb-ui/FormControlText";
import { FormGroup } from "@itandi/itandi-bb-ui/FormGroup";
import { IconButton } from "@itandi/itandi-bb-ui/IconButton";
import { Input } from "@itandi/itandi-bb-ui/Input";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { useId } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import useSWRMutation from "swr/mutation";
import type { MailsCreateParams } from "~/models/CompanyStoreMail";
import { validationSchemaCompanyStoreMail } from "~/models/CompanyStoreMail";
import { isSuccess } from "~/utils/api/api_handler";
import { create as createMails } from "~/utils/api/CompanyStoreMails";
import { generatePath } from "~/utils/generatePath";
import { generateKey } from "~/utils/RandomKeyGenerator";

type Props = {
	onClose: () => void;
};

export function CompanyStoreMailModal({ onClose }: Props) {
	const formId = useId();

	const {
		control,
		register,
		handleSubmit,
		formState: { errors }
	} = useForm<MailsCreateParams>({
		defaultValues: {
			companyStoreMails: [{ email: "", key: generateKey() }]
		},
		mode: "onSubmit",
		resolver: yupResolver(validationSchemaCompanyStoreMail)
	});

	const { fields, append, remove } = useFieldArray({
		control,
		name: "companyStoreMails"
	});

	const { trigger, isMutating } = useSWRMutation(
		generatePath("/company_store_mails"),
		async (_key: string, { arg }: { arg: MailsCreateParams }) =>
			createMails(arg)
	);

	const onSubmit = handleSubmit(async (values) => {
		const result = await trigger(values);
		if (isSuccess(result)) {
			toast.success("追加しました。");
			onClose();
		} else {
			toast.error("エラーが発生しました。");
		}
	});

	return (
		<Modalv2 isOpen onClose={onClose}>
			<ModalHeader title="通知先メールアドレスの追加" />
			<ModalBody>
				<form id={formId} noValidate onSubmit={onSubmit}>
					<FormGroup variant="horizontal">
						{fields.map((field, index) => (
							<FormControl
								chip={index === 0 ? "必須" : undefined}
								isInvalid={!!errors.companyStoreMails?.[index]?.email?.message}
								key={field.id}
								label={index === 0 ? "メールアドレス" : undefined}
							>
								<Flex alignItems="center" gap={designTokens["spacing/s"]}>
									<Input
										type="email"
										width="250px"
										{...register(`companyStoreMails.${index}.email`)}
									/>
									{index !== 0 && (
										<IconButton
											Icon={RiDeleteBinLine}
											onClick={() => {
												remove(index);
											}}
											variant="ghost"
										/>
									)}
								</Flex>
								<FormControlText
									error={errors.companyStoreMails?.[index]?.email?.message}
								/>
							</FormControl>
						))}
					</FormGroup>
					<Button
						height="small"
						m={designTokens["spacing/s"]}
						onClick={() => {
							append({ email: "", key: generateKey() });
						}}
						type="button"
						variant="tertiary"
					>
						追加
					</Button>
				</form>
			</ModalBody>
			<ModalFooter
				leftArea={
					<Button
						disabled={isMutating}
						onClick={onClose}
						type="button"
						variant="secondary"
					>
						キャンセル
					</Button>
				}
				rightArea={
					<Button
						form={formId}
						loading={isMutating}
						type="submit"
						variant="primary"
					>
						追加
					</Button>
				}
			/>
		</Modalv2>
	);
}
